import * as React from 'react'

import { ShapesIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { LocationType } from '~/client/graph'
import {
  HOURS_IN_DAY,
  HOUR_HEIGHT_PX,
  MINUTES_IN_HOUR,
} from '~/client/src/shared/stores/ui/CalendarView.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import Localization from '../../../localization/LocalizationManager'
import { CalendarEventEntityType } from '../../../models/CalendarEvent'
import { ICollectionItem } from '../../../stores/domain/Calendars.store'
import MenuCloser from '../../MenuCloser'
import CalendarGroupByMenu from './CalendarGroupByMenu'

interface IProps {
  dayHours: number[]

  className?: string
  selectedType?: LocationType
  entityType?: CalendarEventEntityType
  onTypeChange?: (type: LocationType) => void

  /** The single date on screen. Omitted by the multi-day gutter, which shares
   * one axis across columns and so can only show the project-wide window. */
  activeDate?: Date

  shouldUseLargeText?: boolean
  shouldHideTimezone?: boolean
  shouldHideProjectHours?: boolean
  projectDateStore?: ProjectDateStore
  filteredAttributes?: ICollectionItem[]
  updateSortedCollection?: (collection: ICollectionItem[]) => void
  isDayCalendarModeActive?: boolean
  setShowingOfAllZoneColumns?: (shouldShowAllAttributesColumns: boolean) => void
  setRef?(ref: HTMLDivElement): void
}

interface IProjectHourStyle {
  marginTop: number
}

/** A boundary the gutter marks, with the hour row it belongs to. */
interface IBoundaryMark {
  time: Date
  hour: number
  minute: number
}

const HOURS_MARGIN_DIFFERENCE = 11
const MAX_PROJECT_HOUR_MARGIN_HEIGHT = 40

@inject('projectDateStore')
@observer
export default class CalendarHoursLabels extends React.Component<IProps> {
  @observable private isMenuShown: boolean = false
  @observable private areEmptyGroupsHidden: boolean = true

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const {
      shouldUseLargeText,
      shouldHideTimezone,
      setRef,
      dayHours,
      projectDateStore: { getFullHourLabel },
      className,
    } = this.props

    return (
      <>
        {!shouldHideTimezone && this.renderTopLeftSelector()}
        <div
          className={classList({
            'hour-labels-container no-grow relative': true,
            [className]: !!className,
          })}
          ref={setRef}
        >
          {dayHours.map(hour => {
            return (
              <div
                key={hour}
                className="col"
                style={{ height: HOUR_HEIGHT_PX }}
              >
                <div
                  className={classList({
                    'text right pr10 full-height': true,
                    large: shouldUseLargeText,
                  })}
                >
                  {getFullHourLabel(hour % HOURS_IN_DAY)}
                </div>
                {this.renderProjectDates(hour)}
              </div>
            )
          })}
        </div>
      </>
    )
  }

  private renderTopLeftSelector() {
    const {
      projectDateStore: { getClientTimezoneOffsetAsString },
      selectedType,
      entityType,
      onTypeChange,
      filteredAttributes,
      updateSortedCollection,
      isDayCalendarModeActive,
    } = this.props

    if (
      isDayCalendarModeActive &&
      entityType === CalendarEventEntityType.Delivery
    ) {
      return (
        <div className="gmt-timezone top5">
          <div
            className={classList({
              'py10 text blue row large pointer brada4': true,
              'bg-blue-light unclickable-element': this.isMenuShown,
            })}
            onClick={this.toggleMenu}
          >
            <ShapesIcon size={16} className="tw:shrink-0 tw:mr-1" />
            <span>{Localization.translator.groupBy}</span>
          </div>
          {this.isMenuShown && (
            <MenuCloser closeMenu={this.toggleMenu}>
              <CalendarGroupByMenu
                selectedType={selectedType}
                onTypeChange={onTypeChange}
                areEmptyGroupsHidden={this.areEmptyGroupsHidden}
                toggleHideEmpty={this.toggleEmptyGroups}
                filteredAttributes={filteredAttributes}
                updateSortedCollection={updateSortedCollection}
              />
            </MenuCloser>
          )}
        </div>
      )
    }

    return (
      <div className="gmt-timezone text bold">
        {getClientTimezoneOffsetAsString()}
      </div>
    )
  }

  @action
  private toggleMenu = () => {
    this.isMenuShown = !this.isMenuShown
  }

  @action
  private toggleEmptyGroups = () => {
    this.areEmptyGroupsHidden = !this.areEmptyGroupsHidden
    this.props.setShowingOfAllZoneColumns(!this.areEmptyGroupsHidden)
  }

  private renderProjectDates(hour: number): JSX.Element {
    const {
      shouldUseLargeText,
      shouldHideProjectHours,
      projectDateStore: { getTimeToDisplay },
    } = this.props

    if (shouldHideProjectHours) {
      return null
    }

    // A whole hour needs no mark — the hour row's own line already sits there.
    const marks = this.boundaryMarks.filter(
      mark => mark.hour === hour && !!mark.minute,
    )

    if (!marks.length) {
      return null
    }

    return (
      <div
        className={classList({
          'absolute col text full-width pr10 opacity5 mt10': true,
          small: !shouldUseLargeText,
        })}
      >
        {marks.map(({ time, minute }, index) => (
          <div
            key={minute}
            className={classList({ 'row x-end': true, mt25: !!index })}
            // Two boundaries inside one hour stack instead, so there is no room
            // to offset the first towards its own minute.
            style={marks.length > 1 ? null : this.getStyle(minute)}
          >
            {getTimeToDisplay(time)}
          </div>
        ))}
      </div>
    )
  }

  /**
   * Where the day's work starts and stops, ascending. The gutter's own extent is
   * a fixed midnight-to-midnight axis, so it stays project-wide, but the ticks
   * mark a per-day fact — and a date can hold two stretches of work, so it can
   * need more than the two marks one window would give (STX1-983).
   */
  private get boundaryMarks(): IBoundaryMark[] {
    const { activeDate, projectDateStore } = this.props
    const { getWorkingBoundaryMinutesForDate, getHoursMinutes, setHours } =
      projectDateStore

    const times = activeDate
      ? getWorkingBoundaryMinutesForDate(activeDate).map(minute =>
          setHours(
            new Date(activeDate),
            Math.floor(minute / MINUTES_IN_HOUR),
            minute % MINUTES_IN_HOUR,
          ),
        )
      : this.projectWindowTimes

    return times
      .map(time => {
        const [hour, minute] = getHoursMinutes(time)

        return { time, hour, minute }
      })
      .sort((a, b) => a.time.getTime() - b.time.getTime())
  }

  /**
   * What the multi-day gutter marks instead: it shares one axis across its
   * columns, so it has no single date to read and stays on the project-wide
   * window. Ends that coincide are the 24-hour day, which has no boundary.
   */
  private get projectWindowTimes(): Date[] {
    const { getHoursMinutes, projectWorkingHours } = this.props.projectDateStore
    const { startTime, endTime } = projectWorkingHours
    const [startHour, startMinute] = getHoursMinutes(startTime)
    const [endHour, endMinute] = getHoursMinutes(endTime)

    return startHour === endHour && startMinute === endMinute
      ? []
      : [startTime, endTime]
  }

  private getStyle = (minutes: number): IProjectHourStyle => {
    let marginTop = 0

    if (minutes > HOURS_MARGIN_DIFFERENCE) {
      marginTop = minutes - HOURS_MARGIN_DIFFERENCE
    }

    if (marginTop > MAX_PROJECT_HOUR_MARGIN_HEIGHT) {
      marginTop = MAX_PROJECT_HOUR_MARGIN_HEIGHT
    }

    return { marginTop }
  }
}
